import React, { useCallback, useEffect, useState } from 'react';
import { Modal, Button } from 'antd';
import { Sparkles, RefreshCw } from 'lucide-react';
import versionControlService, {
  VersionCheckResponse,
} from '../../services/versionControl.service';
import { parseReleaseNotes } from '../../utils/releaseNotes';

/**
 * "A new version is available" popup.
 *
 * The problem it solves: the panel is a single-page app that people leave open
 * for days. index.html is served `no-cache`, so a reload WOULD pick up a new
 * build — but nothing ever tells them to reload, which is why we have been
 * asking clients to hard-refresh by hand after every deploy.
 *
 * Deliberately NOT a blocking modal. It appears on load, never on a timer, so
 * it cannot interrupt a half-filled form; and "Later" dismisses it for this
 * session. publishWebRelease() pins updateType to RECOMMENDED so a release
 * announcement can never become a forced update — but if a genuine force-update
 * is ever configured, `forceUpdate` removes the dismiss path.
 *
 * Notes arrive newline-separated in `releaseNotes` (one line per change) and may
 * end with a line addressed to this tenant specifically, because one web image
 * serves every tenant and a change requested by one client still ships to all.
 */

const DISMISSED_KEY = 'update_modal_dismissed_version';

const UpdateAvailableModal: React.FC = () => {
  const [versionInfo, setVersionInfo] = useState<VersionCheckResponse | null>(
    () => versionControlService.getCachedVersionInfo(),
  );
  const [open, setOpen] = useState(false);
  const [updating, setUpdating] = useState(false);

  const shouldShow = useCallback((info: VersionCheckResponse | null): boolean => {
    if (!info?.updateAvailable) return false;
    if (info.forceUpdate) return true; // not dismissible, always shown
    try {
      return sessionStorage.getItem(DISMISSED_KEY) !== info.latestVersion;
    } catch {
      // Private mode / blocked storage: show it rather than swallow the release.
      return true;
    }
  }, []);

  useEffect(() => {
    setOpen(shouldShow(versionInfo));
    const unsubscribe = versionControlService.subscribe((info) => {
      setVersionInfo(info);
      setOpen(shouldShow(info));
    });
    return () => unsubscribe();
  }, [shouldShow, versionInfo]);

  const handleUpdate = () => {
    setUpdating(true);
    // performUpdate() clears caches and reloads; the brief delay is only so the
    // button visibly acknowledges the click before the page goes away.
    setTimeout(() => versionControlService.performUpdate(), 400);
  };

  const handleLater = () => {
    try {
      if (versionInfo?.latestVersion) {
        sessionStorage.setItem(DISMISSED_KEY, versionInfo.latestVersion);
      }
    } catch {
      /* storage unavailable — it simply reappears on the next load */
    }
    setOpen(false);
  };

  if (!versionInfo?.updateAvailable) return null;

  const notes = parseReleaseNotes(versionInfo.releaseNotes);
  const isForced = !!versionInfo.forceUpdate;

  return (
    <Modal
      open={open}
      centered
      closable={!isForced}
      maskClosable={!isForced}
      keyboard={!isForced}
      onCancel={isForced ? undefined : handleLater}
      footer={null}
      width={460}
    >
      <div className="flex items-start gap-3 mb-4">
        <div className="flex items-center justify-center w-10 h-10 rounded-full bg-blue-50 shrink-0">
          <Sparkles size={20} className="text-blue-600" />
        </div>
        <div>
          <h3 className="text-base font-semibold text-gray-800 mb-0.5">
            {versionInfo.updateTitle || 'Update available'}
          </h3>
          <p className="text-xs text-gray-400">
            Version {versionInfo.latestVersion}
            {versionInfo.clientVersion ? ` · you are on ${versionInfo.clientVersion}` : ''}
          </p>
        </div>
      </div>

      {notes.length > 0 && (
        <div className="rounded-md bg-gray-50 border border-gray-200 p-3 mb-4 max-h-56 overflow-y-auto">
          <p className="text-xs font-medium text-gray-500 mb-2">What&apos;s new</p>
          <ul className="space-y-1.5">
            {notes.map((note, i) => (
              <li key={i} className="flex gap-2 text-sm text-gray-700 leading-snug">
                <span className="text-blue-500 mt-0.5 shrink-0">•</span>
                <span>{note}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <p className="text-xs text-gray-400 mb-4">
        Updating reloads this page. Save anything you are in the middle of first.
      </p>

      <div className="flex justify-end gap-2">
        {!isForced && (
          <Button onClick={handleLater} disabled={updating}>
            Later
          </Button>
        )}
        <Button
          type="primary"
          loading={updating}
          onClick={handleUpdate}
          icon={!updating ? <RefreshCw size={14} /> : undefined}
        >
          {updating ? 'Updating…' : 'Get update now'}
        </Button>
      </div>
    </Modal>
  );
};

export default UpdateAvailableModal;
